import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { canOpenUltratechReports } from './reportsAccess'
import { ChevronDown, ChevronUp, LayoutGrid, Minimize2 } from 'lucide-react'
import { defaultSurfaceFor, EmptyState, fleetPills, pathFor, surfacePills, TopBar, type FleetId } from '../cc'
import { CC } from '../cc/tokens'
import { ReportTable, ReportTablePagination } from '../cc/ReportTable'
import { sessionDisplayName, useSession } from '../auth/useSession'
import { ConnectedAlertsReport } from './alerts/ConnectedAlertsReport'
import { TripRevenueLoss } from '../panels/TripRevenueLoss'
import { BriefingStrip } from './BriefingStrip'
import { RoundTripJourney } from './journey/RoundTripJourney'
import { GeoTagsReport } from './geotags/GeoTagsReport'
import { ExecSummaryDownload } from './ExecSummaryDownload'
import { DorReport } from './dor/DorReport'
import { SPECIALIZED_REPORTS, allReportIds, findCatalogItem } from './reportCatalog'
import { ReportRowDrawer } from './ReportRowDrawer'
import { ReportSidebar } from './ReportSidebar'
import { ReportToolbar } from './ReportToolbar'
import { SpecializedViz } from './specialized'
import { exportCsv, fetchReport, fetchReportIndex, useReportData } from './useReportData'
import { useReportTableState } from './useReportTableState'
import { reportCard, reportCardPad, reportSegment, reportSegmentBtn } from './ui'
import type { ReportRow } from '../lib/types'

export type ReportMode =
  | 'alerts' | 'dor' | 'roundtrips' | 'geotags' | 'losses'
  | 'tables' | 'summary'

const DEFAULT_MODE: ReportMode = 'alerts'
const REPORT_MODES = new Set<string>([
  'alerts', 'dor', 'roundtrips', 'geotags', 'losses', 'tables', 'summary',
])

// Every report tab is open to any signed-in role (user, admin, super admin) -- matches
// serving/app.py's /api/reports/{name}, which no longer gates on is_admin. Geo tags is
// the one exception: it reads raw per-vehicle geofence crossings, not a curated report,
// and stays behind its own separate server-side admin gate (_require_admin_raw_data,
// "geo tags") -- adminOnly here is a courtesy that mirrors that, same as before.
const MODE_TABS: { id: ReportMode; label: string; hint: string; adminOnly?: boolean }[] = [
  { id: 'alerts', label: 'Alerts', hint: 'Who, where and when alerts fire — by driver, vehicle, location, shift and trend — and the trip each one happened in.' },
  { id: 'dor', label: 'Daily DOR', hint: 'Kms, trips, tonnage, loading TAT, breakdowns, drivers and energy for one day — from telemetry and TIMS. Downloads as the Excel workbook.' },
  { id: 'roundtrips', label: 'Round trips', hint: 'The Dhar–Dhule–Dhar lap, measured from geotags — click a stage to drill in.' },
  { id: 'geotags', label: 'Geo tags', hint: 'Every geofence crossing for one vehicle, one window — filter and download the raw record.', adminOnly: true },
  { id: 'losses', label: 'Trip & revenue loss', hint: 'What breakdown downtime actually costs — ranked by fault category and severity.' },
  { id: 'summary', label: 'Downloads', hint: 'Executive summary PDF, plus the round trip, breakdown, and driver Excel reports — weekly or monthly.' },
  { id: 'tables', label: 'Raw data', hint: 'SQL views — same numbers as CSV export.' },
]


function vizKey(id: string): string {
  return `report-viz:${id}`
}

function loadVizCollapsed(id: string): boolean {
  try { return sessionStorage.getItem(vizKey(id)) === 'collapsed' } catch { return false }
}

function saveVizCollapsed(id: string, collapsed: boolean): void {
  try { sessionStorage.setItem(vizKey(id), collapsed ? 'collapsed' : 'expanded') } catch { /* */ }
}

export function ReportsShell() {
  const navigate = useNavigate()
  const routeParams = useParams<{ mode?: string }>()
  // An unknown/missing segment reads as the default rather than a blank page -- a
  // bookmark to a report that got renamed should still land somewhere real.
  const mode: ReportMode = (
    routeParams.mode && REPORT_MODES.has(routeParams.mode) ? routeParams.mode : DEFAULT_MODE
  ) as ReportMode
  const onModeChange = useCallback((m: ReportMode) => navigate(`/reports/${m}`), [navigate])
  const session = useSession()
  // Loading counts as NOT admin, briefly -- the admin-only tabs and their data are hidden
  // for the moment /api/me takes to answer rather than flashed and then pulled away, which
  // reads as a bug rather than a permission check.
  const isAdmin = session.status === 'signed-in' && session.session.is_admin !== false
  // Alerts is the one tab that is not Ultratech-only (its router checks `?site=` against
  // the account's own sites); every other tab reads routes the middleware gates on the
  // Ultratech site, so an account without it sees Alerts alone. Same loading rule as
  // isAdmin: hidden until /api/me answers, never flashed and pulled.
  const sessionReady = session.status === 'signed-in'
  const ultratechReports = sessionReady && canOpenUltratechReports(session.session)
  const visibleTabs = MODE_TABS.filter((t) => (!t.adminOnly || isAdmin) && (t.id === 'alerts' || ultratechReports))
  // A user whose mode is stale (session changed under them, or a deep link) lands on
  // Alerts rather than a tab with nothing behind it -- admin-only tabs for a non-admin,
  // Ultratech tabs for a Wonder-Cement-only account. Only once the session is known: a
  // deep link to /reports/roundtrips must not bounce an Ultratech user while /api/me is
  // still in flight.
  useEffect(() => {
    const tab = MODE_TABS.find((t) => t.id === mode)
    if (tab?.adminOnly && !isAdmin) onModeChange(DEFAULT_MODE)
    else if (sessionReady && !ultratechReports && mode !== 'alerts') onModeChange('alerts')
  }, [mode, isAdmin, sessionReady, ultratechReports, onModeChange])

  const [clock, setClock] = useState(() => new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
  const [reportId, setReportId] = useState('tat_headline')
  const [available, setAvailable] = useState<Set<string>>(() => new Set(allReportIds()))
  const [configured, setConfigured] = useState(true)
  const [briefCollapsed, setBriefCollapsed] = useState(false)
  const [briefTick, setBriefTick] = useState(0)
  const [vizCollapsed, setVizCollapsed] = useState(() => loadVizCollapsed('tat_headline'))
  const [detailRow, setDetailRow] = useState<ReportRow | null>(null)
  const [fullPage, setFullPage] = useState(false)

  const { rows, status, error, fetchedAt, refetch } = useReportData(reportId, {
    limit: 500,
    enabled: mode === 'tables',
    skipCache: false,
  })

  const table = useReportTableState(reportId, rows)

  useEffect(() => {
    const id = window.setInterval(() => {
      setClock(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    void fetchReportIndex().then(({ reports, configured: cfg }) => {
      setConfigured(cfg)
      if (reports.length) setAvailable(new Set(reports))
    })
  }, [])

  useEffect(() => {
    setVizCollapsed(loadVizCollapsed(reportId))
    setDetailRow(null)
  }, [reportId])

  useEffect(() => {
    const id = window.setInterval(() => setBriefTick((t) => t + 1), 45_000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    if (!fullPage) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      if (detailRow) return
      e.preventDefault()
      setFullPage(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullPage, detailRow])

  const handleExport = useCallback(async (limit: number) => {
    const exportRows = limit > 500
      ? await fetchReport(reportId, limit)
      : (table.selectedRows.length ? table.selectedRows : table.sorted)
    const cols = table.columns.length ? table.columns : table.allColumns
    exportCsv(exportRows, cols, reportId)
  }, [reportId, table.selectedRows, table.sorted, table.columns, table.allColumns])

  const handleRefresh = useCallback(() => {
    refetch()
    setBriefTick((t) => t + 1)
  }, [refetch])

  const hasSpecialized = SPECIALIZED_REPORTS.has(reportId)
  const catalog = findCatalogItem(reportId)

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-3 p-3"
      style={{ background: CC.page }}
    >
      {fullPage ? (
        <header className="sticky top-0 z-10 flex shrink-0 flex-wrap items-center gap-2 rounded-[14px] border border-[rgba(15,23,42,0.07)] bg-white px-3 py-2.5 sm:px-4">
          <LayoutGrid size={15} className="shrink-0 text-[#64748B]" />
          <span className="shrink-0 text-[13px] font-semibold text-[#0F172A]">Full page</span>
          {mode === 'tables' ? (
            <>
              <span className="hidden h-4 w-px bg-slate-200 sm:block" />
              <span className="min-w-0 max-w-[12rem] truncate text-[13px] font-medium text-slate-700">
                {catalog?.label ?? reportId}
              </span>
              <ReportSidebar
                activeId={reportId}
                onSelect={setReportId}
                available={available}
                configured={configured}
                rail={false}
              />
            </>
          ) : null}
          <div
            className="flex min-w-0 flex-1 flex-wrap gap-1 rounded-[10px] bg-[#EEF2F7] p-1"
            role="tablist"
            aria-label="Report modes"
          >
            {visibleTabs.map((tab) => {
              const isActive = mode === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onModeChange(tab.id)}
                  className={clsx(
                    'flex h-8 shrink-0 items-center justify-center rounded-[8px] px-2.5 text-[11px] transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
                    isActive
                      ? 'bg-white font-semibold text-[#0F172A] shadow-[0_1px_3px_rgba(15,23,42,0.10)]'
                      : 'font-medium text-[#64748B] hover:text-[#0F172A]',
                  )}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => setFullPage(false)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.10)]
                       bg-[#0F172A] px-2.5 py-1.5 text-[11.5px] font-medium text-white
                       hover:bg-[#1E293B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
          >
            <Minimize2 size={12} />
            Exit full page
          </button>
        </header>
      ) : (
        <>
          {/* The bar does not wrap; on a phone its pills ran ~1,150px wide and widened the
              whole page -- the layout viewport grew with it, so fixed overlays (the alert
              trip drawer) anchored off-screen. Contained here, it scrolls sideways itself. */}
          <div className="min-w-0 max-w-full shrink-0 overflow-x-auto">
          <TopBar
            clock={clock}
            live={configured}
            user={sessionDisplayName(session)}
            fleets={fleetPills(session.status === 'signed-in' && session.session.role === 'super_admin')}
            activeFleet="ultratech"
            onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
            nav={surfacePills('ultratech')}
            activeNav="reports"
            onNav={(id) => navigate(pathFor('ultratech', id === 'reports' ? 'reports' : 'command'))}
            onSettings={() => navigate('/settings')}
          />
          </div>

          <div
            role="tablist"
            aria-label="Report modes"
            className={clsx(reportCard, reportCardPad, 'flex flex-wrap items-center justify-between gap-3')}
          >
            <div className="min-w-0 max-w-full">
              {/* Wraps instead of overflowing: six tabs do not fit a phone's width, and a
                  strip wider than the viewport widened the whole page under it. */}
              <div className={clsx(reportSegment, 'max-w-full flex-wrap')}>
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={mode === tab.id}
                    type="button"
                    onClick={() => onModeChange(tab.id)}
                    className={reportSegmentBtn(mode === tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-sm text-slate-500">
                {MODE_TABS.find((t) => t.id === mode)?.hint}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFullPage(true)}
              className="flex h-8 items-center justify-center gap-1.5 rounded-[8px] border
                          border-[rgba(15,23,42,0.08)] bg-white px-2.5 text-[11px] font-medium text-[#475569]
                          transition-colors hover:border-[rgba(15,23,42,0.16)] hover:text-[#0F172A]
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
              title="Give the report table the full main area"
            >
              <LayoutGrid size={13} />
              Full page
            </button>
          </div>
        </>
      )}

      {mode === 'alerts' ? (
        <div className="min-h-0 flex-1 animate-fade-in overflow-auto">
          <ConnectedAlertsReport />
        </div>
      ) : null}
      {mode === 'dor' ? (
        <div className="min-h-0 flex-1 animate-fade-in overflow-auto">
          <DorReport />
        </div>
      ) : null}
      {mode === 'roundtrips' ? (
        <div className="min-h-0 flex-1 animate-fade-in overflow-auto p-6">
          <RoundTripJourney />
          <div className="mx-auto mt-5 max-w-5xl">
            <div className={`${reportCard} ${reportCardPad}`}>
              <h2 className="text-sm font-semibold text-slate-900">Every number here, as a table</h2>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-500">
                The same geotag-measured views behind the picture above, unrounded and
                exportable.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {([
                  ['round_trip_tat', 'The round trip'],
                  ['cycle_report', 'Every lap'],
                  ['hub_time_split', 'Within a hub'],
                  ['leg_segment_mix', 'Within a road leg'],
                  ['stage_pain', 'Where hours leak'],
                  ['stage_cycle', 'One cycle in order'],
                ] as const).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px]
                               font-medium text-slate-700 transition-[border-color,color,transform] duration-100
                               hover:border-slate-300 hover:text-slate-900 active:scale-[0.97]"
                    onClick={() => { setReportId(id); onModeChange('tables') }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {mode === 'geotags' ? (
        <div className="min-h-0 flex-1 animate-fade-in overflow-auto">
          <GeoTagsReport />
        </div>
      ) : null}
      {mode === 'losses' ? (
        <div className="min-h-0 flex-1 animate-fade-in overflow-auto">
          <TripRevenueLoss />
        </div>
      ) : null}
      {mode === 'summary' ? (
        <div className="min-h-0 flex-1 animate-fade-in overflow-auto">
          <ExecSummaryDownload />
        </div>
      ) : null}

      {mode === 'tables' ? (
        <div className="flex min-h-0 flex-1 animate-fade-in gap-3 overflow-hidden">
          {!fullPage ? (
            <ReportSidebar
              activeId={reportId}
              onSelect={setReportId}
              available={available}
              configured={configured}
            />
          ) : null}

          <div className={clsx(
            'flex min-h-0 min-w-0 flex-1 flex-col gap-3',
            fullPage ? 'overflow-hidden' : 'overflow-auto',
          )}>
            {!fullPage ? (
              <BriefingStrip
                collapsed={briefCollapsed}
                onToggle={() => setBriefCollapsed((c) => !c)}
                refreshTick={briefTick}
              />
            ) : null}

            <div className="shrink-0">
              <ReportToolbar
                reportId={reportId}
                rowCount={table.totalCount}
                selectedCount={table.selectedRows.length}
                filter={table.filter}
                onFilterChange={table.setFilter}
                dayOptions={table.dayOptions}
                dayFilter={table.dayFilter}
                onDayFilterChange={table.setDayFilter}
                allColumns={table.allColumns}
                visibleColumns={table.visibleColumns}
                onToggleColumn={table.toggleColumn}
                onRefresh={handleRefresh}
                onExport={handleExport}
                fetchedAt={fetchedAt}
                loading={status === 'loading'}
                hideTitle={fullPage}
              />
            </div>

            {status === 'error' ? (
              <div className={clsx(reportCard, reportCardPad)}>
                <h3 className="text-sm font-semibold text-slate-900">Reporting database unavailable</h3>
                <p className="mt-3 text-sm leading-relaxed text-red-800">{error}</p>
              </div>
            ) : null}

            {status === 'ok' && !rows.length ? (
              <EmptyState
                title="No rows yet"
                body="Trips, hub visits, and alerts will appear here once recorded."
              />
            ) : null}

            {status === 'ok' && rows.length > 0 && hasSpecialized ? (
              <div className={clsx(reportCard, 'overflow-hidden')}>
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-6 py-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Summary</h3>
                    <p className="text-xs text-slate-500">Visual overview of loaded rows</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !vizCollapsed
                      setVizCollapsed(next)
                      saveVizCollapsed(reportId, next)
                    }}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
                  >
                    {vizCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                    {vizCollapsed ? 'Show' : 'Hide'}
                  </button>
                </div>
                {!vizCollapsed ? (
                  <div className="p-6 pt-4">
                    <SpecializedViz reportId={reportId} rows={rows} />
                  </div>
                ) : null}
              </div>
            ) : null}

            {(status === 'loading' || (status === 'ok' && rows.length > 0)) ? (
              <div className={clsx('flex min-h-0 flex-col gap-3', fullPage && 'flex-1')}>
                <ReportTable
                  columns={table.columns}
                  rows={table.pageRows}
                  sort={table.sort}
                  onSort={table.toggleSort}
                  selectable
                  selected={table.selected}
                  onToggleRow={table.toggleRow}
                  onRowClick={(row) => setDetailRow(row)}
                  rowOffset={table.page * (table.pageSize || table.sorted.length)}
                  accentRow={(row) => reportId === 'tat_breach' && Boolean(row.breached)}
                  loading={status === 'loading'}
                  fill={fullPage}
                  emptyTitle="No matching rows"
                  emptyBody="Clear the filter or pick another day to see data again."
                />
                {status === 'ok' && table.totalCount > 0 ? (
                  <ReportTablePagination
                    page={table.page}
                    totalPages={table.totalPages}
                    pageSize={table.pageSize}
                    pageSizes={table.pageSizes}
                    showingFrom={table.showingFrom}
                    showingTo={table.showingTo}
                    totalCount={table.totalCount}
                    onPageChange={table.setPage}
                    onPageSizeChange={(s) => { table.setPageSize(s); table.setPage(0) }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <ReportRowDrawer
        row={detailRow}
        columns={table.allColumns}
        onClose={() => setDetailRow(null)}
      />
    </div>
  )
}
